import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { Link, useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Button, Notification } from '../../../components'
import subscriptionsController from '../../../api/controllers/subscriptions.controller'
import { getAuthorizationToken } from '../../../utils/jwtToken'
import StepLayout from './StepLayout'
import { avaInterviewSelectors } from '../../../redux'

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`

const SubscriptionInfo = styled.div`
  text-align: center;
  max-width: 500px;
`

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #21304e;
`

const Description = styled.p`
  font-size: 16px;
  color: #737991;
  margin-bottom: 8px;
  line-height: 1.5;
`

const TrialInfo = styled.div`
  background: #f0e4f8;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
  text-align: center;
`

const TrialTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #21304e;
  margin-bottom: 8px;
`

const TrialDescription = styled.div`
  font-size: 14px;
  color: #737991;
  line-height: 1.5;
`

const ConfirmButton = styled(Button)`
  margin: 8px auto;
  min-width: 200px;
`

const LoginPrompt = styled.div`
  text-align: center;
  margin-top: 16px;
`

const LoginLink = styled(Link)`
  color: #6c5ce7;
  text-decoration: underline;

  &:hover {
    color: #5a4fcf;
  }
`

interface IProps {
  backMode: boolean
  goBack: () => void
  goNext: () => void
  interviewToken: string
}

const SubscriptionComponent = ({ backMode, goNext, goBack, interviewToken }: IProps) => {
  const history = useHistory()
  const interview = useSelector(avaInterviewSelectors.data)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [checkingSubscription, setCheckingSubscription] = useState(true)
  const [hasSubscription, setHasSubscription] = useState(false)
  const [checkoutComplete, setCheckoutComplete] = useState(false)

  useEffect(() => {
    // Check if returning from Stripe checkout FIRST
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('checkout_success') === 'true') {
      setCheckoutComplete(true)
      setCheckingSubscription(false) // Skip subscription check
      history.replace(window.location.pathname)
      return // Don't run subscription check if checkout was successful
    }

    // Only check subscription if not returning from checkout
    const checkSubscription = async () => {
      // Check if user is already authenticated
      const token = getAuthorizationToken()
      if (token) {
        // User is authenticated, check their subscription status
        try {
          const subscriptionResponse = await subscriptionsController.getSubscription()
          if (
            subscriptionResponse.subscription &&
            subscriptionResponse.remaining_interviews > 0
          ) {
            // User has subscription and interviews available
            setHasSubscription(true)
            goNext() // Skip to GettingQuestions
            return
          }
        } catch (err) {
          console.error('Error fetching subscription:', err)
        }
      }

      // If not authenticated or no subscription, check by email if available
      if (!interview || !interview.candidate) {
        setCheckingSubscription(false)
        return
      }

      const { email } = interview.candidate
      if (!email) {
        setCheckingSubscription(false)
        return
      }

      try {
        const statusResponse = await subscriptionsController.checkStatus(email)

        if (statusResponse.has_subscription && statusResponse.can_create_interview) {
          // User has subscription but is not authenticated
          // They need to log in to continue - show message
          setCheckingSubscription(false)
          setError(
            'You have an active subscription. Please log in to continue your interview.'
          )
        } else if (
          statusResponse.has_subscription &&
          !statusResponse.can_create_interview
        ) {
          // User has subscription but no interviews left
          setCheckingSubscription(false)
          setError(
            'You have reached your interview limit for this month. Please upgrade your plan or wait for the next billing cycle.'
          )
        } else {
          // No subscription - show subscription step
          setCheckingSubscription(false)
        }
      } catch (err) {
        console.error('Error checking subscription:', err)
        setCheckingSubscription(false)
      }
    }

    checkSubscription()
  }, [interview, goNext, history])

  const handleCheckout = async () => {
    setLoading(true)
    setError(null)

    try {
      // Get email from interview candidate data
      if (!interview || !interview.candidate) {
        setError('Interview data is not available. Please refresh the page.')
        setLoading(false)
        return
      }

      const { email } = interview.candidate
      if (!email) {
        setError('Email is required. Please go back and enter your email.')
        setLoading(false)
        return
      }

      const successUrl = `${window.location.origin}/ava/${interviewToken}?checkout_success=true`
      const cancelUrl = window.location.href

      const response = await subscriptionsController.createCheckoutSession(
        undefined, // Price ID is managed by backend
        successUrl,
        cancelUrl,
        email,
        interview.candidate?.first_name,
        interview.candidate?.last_name,
        interviewToken // Pass interview token
      )

      // Redirect to Stripe Checkout
      window.location.href = response.checkout_url
    } catch (err) {
      const checkoutError = err as any
      setError(
        checkoutError.response?.data?.errors ||
          checkoutError.response?.data?.error ||
          'Failed to create checkout session'
      )
      setLoading(false)
    }
  }

  const clearError = () => {
    setError(null)
  }

  // Show loading state while checking subscription
  if (checkingSubscription) {
    return (
      <StepLayout
        title="Unlock Your Potential"
        subTitle="Now that I understand your goals, let's get you ready to succeed."
        backMode={backMode}
        goBack={goBack}
        canGoBack
      >
        <Container />
      </StepLayout>
    )
  }

  if (hasSubscription) {
    return null
  }

  if (checkoutComplete) {
    return (
      <StepLayout
        title="Check Your Email"
        backMode={false}
        goBack={undefined}
        canGoBack={false}
      >
        <Container>
          <SubscriptionInfo>
            <Title>Subscription Successful!</Title>
            <Description>
              We&apos;ve sent you an email with a link to continue your interview. Please
              check your inbox and click the link to proceed.
            </Description>
            <Description>The link will expire in 48 hours.</Description>
          </SubscriptionInfo>
        </Container>
      </StepLayout>
    )
  }

  return (
    <StepLayout
      title="Unlock Your Potential"
      subTitle="Now that I understand your goals, let's get you ready to succeed."
      goBack={goBack}
      goNext={undefined}
      canGoBack
      disabled
    >
      <Container>
        <SubscriptionInfo>
          <Title>Master Your Interview Skills</Title>
          <Description>
            Join thousands of candidates who have landed their dream jobs. Start with a
            risk-free trial today.
          </Description>
          <TrialInfo>
            <TrialTitle>3-Day Free Trial</TrialTitle>
            <TrialDescription>
              Experience full access to one AI-powered mock interview and detailed
              feedback. Then just $39.99/month for 10 interviews per month. No commitment,
              cancel anytime.
            </TrialDescription>
          </TrialInfo>
          {error && <Notification onClose={clearError} message={error} />}
          {error && error.includes('log in') && (
            <LoginPrompt>
              <LoginLink to="/user/login">Log in here</LoginLink> to continue.
            </LoginPrompt>
          )}
          {!error && (
            <ConfirmButton
              type="button"
              onClick={handleCheckout}
              disabled={loading}
              title={loading ? 'Loading...' : 'Start My Free Trial'}
            />
          )}
        </SubscriptionInfo>
      </Container>
    </StepLayout>
  )
}

export default SubscriptionComponent
